
  import { defineComponent } from "vue";
  import { mapStores } from "pinia";

  import { AxiosResponse } from "axios";
  import { get, isEmpty, isString } from "lodash";
  import { marked } from "marked";
  import moment from "moment";

  import { baseToast } from "@/helpers/toastification";
  import { useMasterLayoutStore } from "@/stores/MasterLayoutStore";
  import BaseProcessComponent from "@/components/processComponents/BaseProcessComponent.vue";
  import PdfGenerationRequest from "@/helpers/clients/PdfGeneration";
  import VueTable from "@/components/VueTable.vue";

  interface Section {
    title: string;
    subtitle?: string;
    headers: string[];
    rows: string[];
  }

  const pdfGenerationRequestClient = new PdfGenerationRequest();

  export default defineComponent({
    name: "QuoteResultsRender",

    components: { VueTable },

    extends: BaseProcessComponent,

    computed: {
      ...mapStores(useMasterLayoutStore),

      sections(): Section[] {
        const sections = (this.$attrs.modelValue || []) as Section[];

        return JSON.parse(JSON.stringify(sections), (_: string, value: any): any => {
          if (typeof value === "string") {
            return marked.parse(value);
          }

          return value;
        });
      },
    },

    methods: {
      async download() {
        try {
          this.masterLayoutStore.toggleSpinner();
          const requestGenerationResponse: AxiosResponse = await pdfGenerationRequestClient.createGenerationRequest({
            source_type: "template",
            settings: {
              template: process.env.VUE_APP_QUOTE_TEMPLATE_PATH,
              context: {
                customer: {
                  name: `${get(this, "applicationStore.application.applicants_name")}`,
                  identification_number: "-",
                  type: "-",
                },
                quote: {
                  currency: `${get(this, "applicationStore.application.custom_attributes.currency.detalle")}`,
                  payments_number: 0,
                  amount: `${get(this, "applicationStore.application.custom_attributes.insured_amount")}`,
                  quote_number: `${get(this, "applicationStore.application.custom_attributes.quote_number")}`,
                  created_at: moment(get(this, "applicationStore.application.created_at", "")).format("YYYY-MM-DD"),
                },
                vehicle: {
                  type: `${get(this, "applicationStore.application.custom_attributes.vehicle_type.detalle")}`,
                  brand: `${get(this, "applicationStore.application.custom_attributes.vehicle_brand.detalle")}`,
                  model: `${get(this, "applicationStore.application.custom_attributes.vehicle_model.detalle")}`,
                  year: `${get(this, "applicationStore.application.custom_attributes.vehicle_year.detalle")}`,
                  occupants: get(this, "applicationStore.application.custom_attributes.seating_capacity"),
                  condition: get(this, "applicationStore.application.process.name"),
                  special_equipment_amount: `${get(
                    this,
                    "applicationStore.application.custom_attributes.special_equipment_cost"
                  )}`,
                  special_equipment:
                    get(this, "applicationStore.application.custom_attributes.special_equipment_cost", 0) > 0,
                  armored: false,
                  exempted: false,
                },
                sections: this.sections,
              },
              arguments: [],
            },
          });
          let attempt = 0;
          const maxRetries = 15;
          const delay = 3000;

          let result: AxiosResponse | null = null;

          while (attempt < maxRetries) {
            try {
              const response: AxiosResponse = await pdfGenerationRequestClient.readGenerationRequest(
                requestGenerationResponse.data.id
              );

              const target = get(response, "data.files.target", null);

              if (isString(target) && !isEmpty(target)) {
                result = response;
                const link = document.createElement("a");
                link.href = target;
                link.target = "_blank";
                link.download = "Cotización.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(target);
                this.masterLayoutStore.toggleSpinner();
                break;
              }
            } catch (error) {
              this.masterLayoutStore.toggleSpinner();
              break;
            }

            attempt++;

            if (attempt < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }

          if (!result) {
            this.masterLayoutStore.toggleSpinner();
            baseToast({
              title: "Error al descargar documento",
            });
          }
        } catch (e) {
          this.masterLayoutStore.toggleSpinner();
          baseToast({
            title: "Error al descargar documento",
          });
        }
      },
    },
  });
